import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info);
  }

  handleReload = () => {
    // Likely a stale lazy-chunk after deploy — hard reload to fetch fresh HTML.
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const isChunkError =
      /Loading chunk|Failed to fetch dynamically imported module|ChunkLoadError|Importing a module script failed/i.test(
        error.message,
      );

    return (
      <div
        role="alert"
        className="min-h-screen flex items-center justify-center bg-background p-6"
      >
        <div className="w-full max-w-md space-y-4 rounded-xl border border-border bg-card p-6 text-center">
          <h1 className="font-heading text-xl font-bold">
            {isChunkError ? 'Neue Version verfügbar' : 'Etwas ist schiefgelaufen'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isChunkError
              ? 'Die Seite wurde aktualisiert. Bitte lade neu, um fortzufahren.'
              : error.message}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Neu laden
          </button>
        </div>
      </div>
    );
  }
}
